<template>
  <v-row class="pt-2">
    <v-col cols="12">
      <v-hover v-slot="{ hover }">
        <v-card :elevation="hover ? 6 : 2" :disabled="loading">
          <v-card-title>
            <v-col cols="12" md="4" lg="3">
              <v-select
                class="mt-6"
                dense
                solo
                :disabled="loading"
                v-model="selectedAsset"
                :items="assets"
                item-text="assetName"
                return-object
                placeholder="เลือกตู้แช่ยา"
              />
            </v-col>
            <v-col cols="12" md="2">
              <v-select
                class="mt-6"
                dense
                solo
                :disabled="loading"
                v-model="selectedPeriod"
                :items="periods"
                item-text="period"
                item-value="value"
              />
            </v-col>
            <v-col cols="12" md="auto">
              <DateRangePicker class="mt-6" v-model="range" />
            </v-col>
            <v-col cols="12" md="auto">
              <v-btn
                color="primary"
                :disabled="loading || !selectedAsset"
                @click="getHistory()"
                :block="$vuetify.breakpoint.mobile"
              >
                View
              </v-btn>
            </v-col>
            <v-col cols="12" md="auto">
              <v-btn
                color="primary"
                outlined
                :disabled="loading || items.length === 0"
                @click="exportHistory()"
                :block="$vuetify.breakpoint.mobile"
              >
                Export
              </v-btn>
            </v-col>
          </v-card-title>
        </v-card>
      </v-hover>
    </v-col>

    <v-col cols="12">
      <v-hover v-slot="{ hover }">
        <v-card :elevation="hover ? 6 : 2">
          <v-card-text>
            <div ref="chart" class="history-chart"></div>
          </v-card-text>
        </v-card>
      </v-hover>
    </v-col>

    <v-col cols="12">
      <v-hover v-slot="{ hover }">
        <v-card :elevation="hover ? 6 : 2">
          <v-card-text>
            <v-data-table :headers="headers" :items="items" :items-per-page="-1" :loading="loading">
              <template v-slot:item.date="{ item }">
                {{ $dayjs(item.date, "YY-MM-DD").format('DD/MM/YYYY') }}
              </template>
              <template v-slot:item.MinTemp="{ item }">
                {{ Number(item.MinTemp).toFixed(2) }}
              </template>
              <template v-slot:item.MaxTemp="{ item }">
                {{ Number(item.MaxTemp).toFixed(2) }}
              </template>
              <template v-slot:item.AVGTemp="{ item }">
                {{ Number(item.AVGTemp).toFixed(2) }}
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-hover>
    </v-col>
  </v-row>
</template>

<script>
import * as echarts from 'echarts'
import { mapState } from 'vuex'
import DateRangePicker from '@/components/common/DateRangePicker.vue'
import { xlsxMixins } from '@/mixins/xlsx'

export default {
  name: 'MedicineRefrigeratorHistory',
  mixins: [xlsxMixins],
  components: {
    DateRangePicker
  },
  data() {
    return {
      loading: false,
      selectedAsset: null,
      selectedPeriod: 'daily',
      periods: [
        { period: 'Hourly', value: 'hourly' },
        { period: 'Daily', value: 'daily' }
      ],
      range: {
        startDate: this.$dayjs().startOf('month').toDate(),
        endDate: this.$dayjs().endOf('day').toDate()
      },
      headers: [],
      items: [],
      series: {
        axis: [],
        min: [],
        max: [],
        avg: []
      },
      chart: null
    }
  },
  computed: {
    ...mapState('Asset', ['assetMedicineRefrigerator']),
    assets() {
      return this.assetMedicineRefrigerator || []
    },
    building() {
      return this.$store.state.User.building
    }
  },
  watch: {
    assets: {
      immediate: true,
      handler(newValue) {
        if (!this.selectedAsset && newValue && newValue.length > 0) {
          this.selectedAsset = newValue[0]
          this.getHistory()
        }
      }
    },
    selectedPeriod() {
      if (this.selectedAsset) this.getHistory()
    }
  },
  mounted() {
    this.initChart()
  },
  beforeDestroy() {
    if (this.chart) this.chart.dispose()
  },
  methods: {
    initChart() {
      if (!this.$refs.chart) return
      this.chart = echarts.init(this.$refs.chart)
      this.chart.setOption({
        tooltip: {
          trigger: 'axis'
        },
        legend: {
          data: ['Min', 'Max', 'Avg']
        },
        grid: {
          left: '3%',
          right: '3%',
          bottom: '3%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          data: []
        },
        yAxis: {
          type: 'value',
          name: 'Temperature (°C)'
        },
        series: []
      })
    },
    setChart() {
      if (!this.chart) this.initChart()
      if (!this.chart) return
      this.chart.setOption({
        xAxis: {
          data: this.series.axis
        },
        series: [
          {
            name: 'Min',
            type: 'line',
            smooth: true,
            data: this.series.min
          },
          {
            name: 'Max',
            type: 'line',
            smooth: true,
            data: this.series.max
          },
          {
            name: 'Avg',
            type: 'bar',
            data: this.series.avg
          }
        ]
      })
      this.chart.resize()
    },
    getHistory() {
      if (!this.selectedAsset) return
      this.loading = true
      this.$store.dispatch('MedicineRefrigerator/GET_MEDICINE_REFRIGERATOR', {
        BuildingId: this.building.BuildingId,
        type: this.selectedPeriod,
        assetId: this.selectedAsset.assetId,
        start: this.range.startDate.getTime(),
        end: this.range.endDate.getTime()
      }).then((data) => {
        const rows = Array.isArray(data) ? data : []
        this.items = rows
        this.series.axis = []
        this.series.min = []
        this.series.max = []
        this.series.avg = []

        if (this.selectedPeriod === 'hourly') {
          this.headers = [
            { text: 'Date', value: 'date' },
            { text: 'H', value: 'H' },
            { text: 'Min Temp (°C)', value: 'MinTemp' },
            { text: 'Max Temp (°C)', value: 'MaxTemp' },
            { text: 'AVG Temp (°C)', value: 'AVGTemp' }
          ]
          rows.forEach((element) => {
            this.series.axis.push(`${element.date}_${element.H}`)
            this.series.min.push(Number(element.MinTemp))
            this.series.max.push(Number(element.MaxTemp))
            this.series.avg.push(Number(element.AVGTemp))
          })
        } else {
          this.headers = [
            { text: 'Date', value: 'date' },
            { text: 'Min Temp (°C)', value: 'MinTemp' },
            { text: 'Max Temp (°C)', value: 'MaxTemp' },
            { text: 'AVG Temp (°C)', value: 'AVGTemp' }
          ]
          rows.forEach((element) => {
            this.series.axis.push(element.date)
            this.series.min.push(Number(element.MinTemp))
            this.series.max.push(Number(element.MaxTemp))
            this.series.avg.push(Number(element.AVGTemp))
          })
        }
        this.$nextTick(() => this.setChart())
        this.loading = false
      }).catch(() => {
        this.items = []
        this.series.axis = []
        this.series.min = []
        this.series.max = []
        this.series.avg = []
        this.$nextTick(() => this.setChart())
        this.loading = false
      })
    },
    exportHistory() {
      this.exportToXLS(
        `MedicineRefrigeratorHistory_${this.$dayjs().format('DDMMMYYYYHHmmss')}`,
        { headers: this.headers, items: this.items }
      )
    }
  }
}
</script>

<style scoped>
.history-chart {
  width: 100%;
  height: 350px;
}
</style>
